/**
 * Learning Stages Admin Interface
 * 
 * Allows @nodoublesgolfco.com users to manage user learning stages
 * for testing and manual assessment
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { type LearningStage, type UserLearningStage } from '@/types/enhanced-drill';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Search, Save, User, AlertTriangle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { Navigate } from 'react-router-dom';

export default function LearningStagesAdmin() {
  const { isPlatformAdmin, isLoading: adminLoading } = useAdminAccess();
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userStages, setUserStages] = useState<UserLearningStage[]>([]);
  const [drills, setDrills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Load drills
  useEffect(() => {
    loadDrills();
  }, []);

  async function loadDrills() {
    const { data, error } = await supabase
      .from('drills')
      .select('id, title, module_id')
      .not('module_id', 'is', null)
      .order('module_id, title');

    if (error) {
      console.error('Error loading drills:', error);
      return;
    }

    setDrills(data || []);
  }

  async function searchUser() {
    if (!searchEmail) {
      toast({
        title: 'Error',
        description: 'Please enter a user ID',
        variant: 'destructive',
      });
      return;
    }

    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(searchEmail.trim())) {
      toast({
        title: 'Invalid Format',
        description: 'Please enter a valid UUID (example: 00000000-0000-0000-0000-000000000000)',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const userId = searchEmail.trim();
      setSelectedUserId(userId);

      // Load user's learning stages
      const { data: stages, error: stagesError } = await supabase
        .from('user_learning_stages')
        .select('*')
        .eq('user_id', userId)
        .order('drill_id');

      if (stagesError) throw stagesError;

      setUserStages(stages || []);

      toast({
        title: 'User Loaded',
        description: `Found ${stages?.length || 0} learning stage records for this user`,
      });
    } catch (error: any) {
      console.error('Error loading user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load user data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function updateLearningStage(drillId: string, stage: LearningStage, confidenceScore?: number) {
    if (!selectedUserId) return;

    setLoading(true);

    try {
      const { error } = await supabase
        .from('user_learning_stages')
        .upsert({
          user_id: selectedUserId,
          drill_id: drillId,
          learning_stage: stage,
          confidence_score: confidenceScore,
          assessment_date: new Date().toISOString(),
        });

      if (error) throw error;

      // Reload stages
      const { data: stages } = await supabase
        .from('user_learning_stages')
        .select('*')
        .eq('user_id', selectedUserId)
        .order('drill_id');

      setUserStages(stages || []);

      toast({
        title: 'Success',
        description: 'Learning stage updated',
      });
    } catch (error: any) {
      console.error('Error updating learning stage:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update learning stage',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  if (adminLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Learning Stages Admin
          </CardTitle>
          <CardDescription>
            Manage user learning stages for testing and assessment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* User Search */}
          <Alert>
            <AlertDescription className="text-sm">
              <strong>Note:</strong> Enter the User ID from Supabase Dashboard → Authentication → Users (UUID format)
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="email">User ID (UUID)</Label>
              <Input
                id="email"
                type="text"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUser()}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={searchUser} disabled={loading}>
                <Search className="h-4 w-4 mr-2" />
                Load User
              </Button>
            </div>
          </div>

          {/* User Stages Table */}
          {selectedUserId && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Learning Stages</h3>
                <Badge variant="outline">{userStages.length} drills</Badge>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Drill</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Current Stage</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drills.map((drill) => {
                      const userStage = userStages.find(s => s.drill_id === drill.id);
                      return (
                        <TableRow key={drill.id}>
                          <TableCell className="font-medium">{drill.title}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{drill.module_id}</Badge>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={userStage?.learning_stage || 'cognitive'}
                              onValueChange={(value) => 
                                updateLearningStage(drill.id, value as LearningStage)
                              }
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cognitive">Cognitive</SelectItem>
                                <SelectItem value="associative">Associative</SelectItem>
                                <SelectItem value="autonomous">Autonomous</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {userStage?.confidence_score ? 
                              `${(userStage.confidence_score * 100).toFixed(0)}%` : 
                              '-'
                            }
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {userStage?.updated_at ? 
                              new Date(userStage.updated_at).toLocaleDateString() : 
                              'Not set'
                            }
                          </TableCell>
                          <TableCell>
                            {!userStage && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateLearningStage(drill.id, 'cognitive')}
                              >
                                Initialize
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

